import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import ApplicationHeader from '@rio-cloud/rio-uikit/lib/es/ApplicationHeader';
import { CookieDisclaimer, DefaultUserMenu, EulaConsent } from '@rio-cloud/rio-user-menu-component';
import IframeResizer from 'iframe-resizer-react';

import { config } from '@/config';
import { getLocale } from '@/configuration/lang/langSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { useCookiesDisclaimer } from '@/hooks/useCookiesDisclaimer';
import { FeatureToggle, useFeatureToggle } from '@/hooks/useFeatureToggle';
import { useHomeWorkshop } from '@/hooks/useHomeWorkshop';
import FeatureToggleInfoItem from './FeatureToggleInfoItem';
import ServiceInfoItem from './ServiceInfoItem';
import { SettingsIcon } from './settings/SettingsIcon';
import { useStoredReleaseNotes } from './useStoredReleaseNotes';

const AppHeader = () => {
    const { featureToggleValue: isStencilTestFeatureEnabled } = useFeatureToggle(FeatureToggle.ENABLE_STENCIL_TEST);
    const { homeWorkshop, isLoading, error } = useHomeWorkshop();
    const { notSeenReleaseNotes } = useStoredReleaseNotes();
    const hasHomeWorkshop = error ? true : homeWorkshop?.hasHomeWorkshop;
    const { enableCookieDisclaimer, setEnableCookieDisclaimer } = useCookiesDisclaimer();
    const disableNavItem = 'disabled-nav-item';

    const navItems = [
        {
            key: 'overview',
            route: (
                <NavLink to="/overview" className={hasHomeWorkshop ? '' : disableNavItem}>
                    <FormattedMessage id="intl-msg:fleetstatus.app.page.overview" />
                </NavLink>
            ),
        },
        {
            key: 'diagnostics',
            route: (
                <NavLink to="/diagnostics" className={hasHomeWorkshop ? '' : disableNavItem}>
                    <FormattedMessage id="intl-msg:fleetstatus.app.page.diagnostics" />
                </NavLink>
            ),
        },
        {
            key: 'damages',
            route: (
                <NavLink to="/damages" className={hasHomeWorkshop ? '' : disableNavItem}>
                    <FormattedMessage id="intl-msg:fleetstatus.app.page.damages" />
                </NavLink>
            ),
        },
        {
            key: 'myfleet',
            route: (
                <NavLink to="/myfleet" className={hasHomeWorkshop ? '' : disableNavItem}>
                    <FormattedMessage id="intl-msg:fleetstatus.app.page.myfleet" />
                </NavLink>
            ),
        },
    ];
    if (isStencilTestFeatureEnabled) {
        navItems.push({
            key: 'stencil-test',
            route: (
                <NavLink to="/stencil-test" className={hasHomeWorkshop ? '' : disableNavItem}>
                    <FormattedMessage id="intl-msg:fleetstatus.app.page.stencil_test" />
                </NavLink>
            ),
        });
    }

    const environment = import.meta.env.PROD ? 'production' : 'local';
    const userLocale = useAppSelector(getLocale);
    const userMenu = <DefaultUserMenu environment={environment} locale={userLocale} disableCookieModal />;
    const menuUrl = config.backend.MENU_SERVICE as string;
    const appNavigator = <IframeResizer className="iFrameResizer" src={menuUrl} />;
    const homeLink = <a href="/" />;
    const settings = <SettingsIcon />;
    const notifications = <RioNotifications />;

    return (
        <>
            {enableCookieDisclaimer && <CookieDisclaimer environment={environment} locale={userLocale} setShowCookieDisclaimer={setEnableCookieDisclaimer} />}
            <ApplicationHeader
                label={<FormattedMessage id="intl-msg:fleetstatus.global.module_name" />}
                appNavigator={appNavigator}
                homeRoute={homeLink}
                navItems={isLoading ? [] : navItems}
                // eslint-disable-next-line max-len
                actionBarItems={[<FeatureToggleInfoItem key={0} />, settings, <ServiceInfoItem notSeenReleaseNotes={notSeenReleaseNotes} key={1} />, notifications, userMenu]}
            />
            <EulaConsent environment={environment} />
        </>
    );
};

export default AppHeader;
